import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { LeftNavigation } from '@/components/LeftNavigation';
import { TopNavigation } from '@/components/TopNavigation';
import { PageHeader } from '@/components/common/PageHeader';
import { PageHeaderProvider, usePageHeader } from '@/hooks/usePageHeader';
import { useCompany } from '@/hooks/useCompany';
import { useToast } from '@/hooks/useToast';
import { routes, Section } from '@/utils/routes';
import { UserSettingsDrawer } from '@/components/common/UserSettingsDrawer';
const PersistentHeader = () => {
    const { header } = usePageHeader();
    if (!header.items.length && !header.actions)
        return null;
    return (_jsx("div", { className: "max-w-[85rem] mx-auto px-4 sm:px-6 pt-6", children: _jsx(PageHeader, { items: header.items, actions: header.actions }) }));
};
export const WorkspaceShell = ({ clientCompanyId, workspaceId, activeSection, detailFallbackSection, children, }) => {
    const location = useLocation();
    const { workspaces, currentWorkspaceId, setCurrentWorkspace, authOrganizationId, hasEnabledAuthConnections } = useCompany();
    const { showInfo } = useToast();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
    const handleMobileMenuToggle = () => setIsMobileMenuOpen(prev => !prev);
    const handleSectionChange = (section) => {
        if (section === Section.Admin) {
            location.route(`/company/${clientCompanyId}/settings/company`);
            return;
        }
        location.route(`/company/${clientCompanyId}/workspace/${currentWorkspaceId || workspaceId}/${section}`);
    };
    const handleWorkspaceChange = (ws) => {
        setCurrentWorkspace(ws.Id);
        const targetSection = detailFallbackSection || activeSection;
        location.route(`/company/${clientCompanyId}/workspace/${ws.Id}/${targetSection}`);
    };
    // Check for auth organization setup
    useEffect(() => {
        // Wait for auth organization ID to be loaded (not undefined)
        if (authOrganizationId === undefined)
            return;
        // If company not configured with auth organization OR no enabled auth connections, redirect to settings
        if (authOrganizationId === null || !hasEnabledAuthConnections) {
            showInfo('Setup required', 'This company is not fully configured yet. Redirected to Authentication settings.', 7000);
            location.route(routes.settingsAuthentication(clientCompanyId));
            return;
        }
    }, [clientCompanyId, authOrganizationId, hasEnabledAuthConnections]);
    useEffect(() => {
        if (workspaces && workspaces.length > 0) {
            // Ensure currentWorkspaceId is valid
            if (!currentWorkspaceId || !workspaces.some(w => w.Id === currentWorkspaceId)) {
                const nextId = workspaces[0].Id;
                setCurrentWorkspace(nextId);
                location.route(`/company/${clientCompanyId}/workspace/${nextId}/${activeSection}`);
            }
        }
    }, [workspaces, currentWorkspaceId]);
    return (_jsxs("div", { className: "min-h-screen bg-slate-50 dark:bg-slate-900 flex overflow-x-hidden", children: [_jsx(LeftNavigation, { activeSection: activeSection, onSectionChange: (s) => { handleSectionChange(s); setIsMobileMenuOpen(false); }, isOpen: isMobileMenuOpen, onToggle: handleMobileMenuToggle, clientCompanyId: clientCompanyId, onWorkspaceChange: handleWorkspaceChange }), _jsxs("div", { className: "flex-1 lg:ml-64 flex flex-col min-w-0", children: [_jsx(TopNavigation, { onMenuToggle: handleMobileMenuToggle, onSettingsClick: () => setIsUserSettingsOpen(true), onWorkspaceChange: handleWorkspaceChange }), _jsx("main", { className: "flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-800", children: _jsxs(PageHeaderProvider, { children: [_jsx(PersistentHeader, {}), _jsx("div", { className: "max-w-[85rem] mx-auto overflow-hidden", children: children })] }) })] }), _jsx(UserSettingsDrawer, { isOpen: isUserSettingsOpen, onClose: () => setIsUserSettingsOpen(false) })] }));
};
